import { locale } from "svelte-i18n";
import { get } from "svelte/store";
import { getDecimalSeparator } from "./i18n";
import { parseBigInt } from "@shared";
export function validateTokenInput(value, powTenPerWhole) {
    const [replacementText, amount] = validateInput(value, powTenPerWhole);
    return {
        replacementText,
        amount,
    };
}
function validateInput(value, powTenPerWhole) {
    value = value.trim();
    if (value?.length > 0 && value[0] !== "-") {
        const parts = value.split(decimalSeparatorsRegex);
        if (parts.length === 1) {
            const integralString = parts[0];
            const integral = parseBigInt(integralString);
            if (integral !== undefined) {
                return [undefined, integral * BigInt(Math.pow(10, powTenPerWhole))];
            }
        }
        if (parts.length === 2) {
            const integralString = parts[0];
            const integral = parseBigInt(integralString);
            let fractionalString = parts[1];
            let replaceText = false;
            // Trim the string if it exceeds the max number of decimals
            if (fractionalString.length > powTenPerWhole) {
                fractionalString = fractionalString.substr(0, powTenPerWhole);
                replaceText = true;
            }
            const fractional = parseBigInt(fractionalString);
            if (integral !== undefined && fractional !== undefined) {
                const total = integral * BigInt(Math.pow(10, powTenPerWhole)) +
                    fractional * BigInt(Math.pow(10, powTenPerWhole - fractionalString.length));
                return [replaceText ? integralString + "." + fractionalString : undefined, total];
            }
        }
    }
    return ["", BigInt(0)];
}
export function formatTokens(amount, powTenPerWhole, decimalSeparatorOverride, fullPrecision = false) {
    if (amount < 0) {
        amount = BigInt(0);
    }
    return format(amount, powTenPerWhole, decimalSeparatorOverride, fullPrecision);
}
function format(units, powTenPerWhole, decimalSeparatorOverride, fullPrecision) {
    // This is a bespoke notion of significant figures!
    const maxSignificantFigures = 6;
    // 1. Always show the full integral part of the number
    // 2. If the integral part >= 6 digits then remove the fractional part
    // 3. Otherwise if there is an integral part the max total number of digits (integral + fractional) = 6
    // 4. If there is no integral part then the max number of significant figures = 6
    // 5. Pad the fractional part with up to 2 '0's trying to keep the total number of digits <= 6
    const unitsPerWhole = BigInt(Math.pow(10, powTenPerWhole));
    const decimalSeparator = decimalSeparatorOverride ?? getDecimalSeparator(get(locale));
    const integral = units / unitsPerWhole;
    const integralString = integral.toString();
    const fractional = units % unitsPerWhole;
    let fractionalString = fractional.toString().padStart(powTenPerWhole, "0");
    if (!fullPrecision) {
        if (integral > 0) {
            const maxFractionalDecimalPlaces = Math.max(maxSignificantFigures - integralString.length, 0);
            if (fractionalString.length > maxFractionalDecimalPlaces) {
                fractionalString = fractionalString.substring(0, maxFractionalDecimalPlaces);
            }
        }
        else {
            const significantFigures = fractionalString.replace(/^0+/, "").length;
            if (significantFigures > maxSignificantFigures) {
                const indexToRemove = maxSignificantFigures + fractionalString.length - significantFigures;
                fractionalString = fractionalString.substring(0, indexToRemove);
            }
        }
    }
    // Remove trailing zeros leaving 0, 1, or 2 depending on how many integral digits we have already
    const minDecimalPlaces = Math.max(0, Math.min(2, maxSignificantFigures - integralString.length));
    for (let i = fractionalString.length - 1; i >= minDecimalPlaces; i--) {
        if (fractionalString[i] === "0") {
            fractionalString = fractionalString.slice(0, -1);
        }
        else {
            break;
        }
    }
    return fractionalString.length > 0
        ? integralString + decimalSeparator + fractionalString
        : integralString;
}
const decimalSeparatorsRegex = /[.,]/;
// The dollar value of `e8s` to 2dp, or "???" if the token's dollar price is unknown
export function calculateDollarAmount(e8s, exchangeRate, decimals) {
    if (exchangeRate === undefined)
        return "???";
    const tokens = Number(e8s) / Math.pow(10, decimals);
    const dollar = tokens * exchangeRate;
    return dollar.toFixed(2);
}
// A token's price in dollars: to 2 or 3 significant figures below $1, since small tokens' prices run
// to many decimal places, to the cent up to $1,000, and to the dollar above that. A price that rounds
// to $1 shows as $1.00, as a dollar stablecoin's does on either side of it
export function formatUsdPrice(usd) {
    const digits = usd < 0.9995
        ? { minimumSignificantDigits: 2, maximumSignificantDigits: 3 }
        : usd < 1000
            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            : { maximumFractionDigits: 0 };
    return usd.toLocaleString("en-US", { style: "currency", currency: "USD", ...digits });
}
// A change in a price, in percent, signed and to 1 decimal place, eg. "+2.9%" or "-12.0%"
export function formatPercentChange(percent) {
    return (percent / 100).toLocaleString("en-US", {
        style: "percent",
        signDisplay: "exceptZero",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
}
